import React, { useEffect, useCallback } from "react";
import {
	DarkWhiteLogoIcon,
	LoginIcon,
	LogoLightNavIcon,
	MoonIcon,
	SunIcon,
} from "@assets/icons";
import P from "@components/common/P/P";
import SvgIcon from "@components/common/SvgIcon/SvgIcon";
import { useTheme } from "hooks/useTheme";
import { scrollToSection } from "@utils/scrollToSection";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "constants/routes";
import { RootState } from "store/store";
import { useSelector } from "react-redux";
import LanguageSwitcher from "@components/common/LanguageSwitcher/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { useLandingSearchNav } from "hooks/useLandingSearchNav";

interface NavbarProps {
	onOpenLogin: () => void;
	onOpenRegister: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onOpenLogin, onOpenRegister }) => {
	const { theme, toggleTheme } = useTheme();
	const location = useLocation();
	const navigate = useNavigate();

	const { t } = useTranslation();

	const isAuthenticated = useSelector(
		(state: RootState) => state.auth.isAuthenticated,
	);

	const { handleLandingSearchClick } = useLandingSearchNav(onOpenRegister);

	useEffect(() => {
		const openRegister = () => onOpenRegister();
		window.addEventListener(
			"open-register-modal",
			openRegister as EventListener,
		);
		return () =>
			window.removeEventListener(
				"open-register-modal",
				openRegister as EventListener,
			);
	}, [onOpenRegister]);

	const goOrScroll = useCallback(
		(section?: string) => {
			if (location.pathname === "/") {
				if (section) scrollToSection(section);
				else window.scrollTo({ top: 0, behavior: "smooth" });
			} else {
				navigate("/", { state: { scrollTo: section } });
			}
		},
		[location.pathname, navigate],
	);

	return (
		<header className="bg-light-bgColor dark:bg-dark-bgColor">
			<nav className="grid grid-flow-row grid-cols-1 gap-4 auto-rows-max">
				{/* TOP BAR */}
				<div className="grid gap-4 py-2 grid-cols-maxTwo place-content-between place-items-center">
					<div className="grid gap-6 grid-cols-maxTwo place-items-center">
						<P
							size="t1"
							type="semibold"
							className="cursor-pointer"
							onClick={() => handleLandingSearchClick("cargo")}
						>
							{t("landing.nav.findCargo")}
						</P>

						<P
							size="t1"
							type="semibold"
							className="cursor-pointer"
							onClick={() => handleLandingSearchClick("transport")}
						>
							{t("landing.nav.findTransport")}
						</P>
					</div>

					<div className="grid gap-3 grid-cols-maxThree place-items-center">
						{/* Language switcher (Landing variant) */}

						{isAuthenticated ? (
							<div
								className="bg-light-mainColor dark:bg-dark-mainColor py-2.5 px-4 rounded-full cursor-pointer"
								onClick={() => navigate(ROUTES.MAIN)}
							>
								<P
									size="t1"
									type="semibold"
									className="text-light-bgColor dark:text-dark-bgColor"
								>
									{t("landing.nav.platform")}
								</P>
							</div>
						) : (
							<>
								<div
									onClick={onOpenLogin}
									className="grid gap-2 cursor-pointer grid-cols-maxTwo place-items-center"
								>
									<SvgIcon
										Icon={LoginIcon}
										className="stroke-light-mainColor dark:stroke-dark-mainColor"
									/>
									<P
										size="t1"
										type="semibold"
										className="text-light-mainColor dark:text-dark-mainColor"
									>
										{t("auth.login.login")}
									</P>
								</div>

								<div
									className="bg-light-mainColor dark:bg-dark-mainColor py-2.5 px-4 rounded-full cursor-pointer"
									onClick={onOpenRegister}
								>
									<P
										size="t1"
										type="semibold"
										className="text-light-bgColor dark:text-dark-bgColor"
									>
										{t("auth.register.register")}
									</P>
								</div>
							</>
						)}

						{/* Theme toggle */}
						<div
							className="bg-light-mainColor dark:bg-dark-mainColor p-2.5 rounded-full cursor-pointer"
							onClick={toggleTheme}
							aria-label={t("common.toggleTheme")}
						>
							{theme === "light" ? (
								<SvgIcon Icon={MoonIcon} />
							) : (
								<SvgIcon Icon={SunIcon} />
							)}
						</div>
					</div>
				</div>

				{/* NAV LINKS */}
				<div className="grid gap-8 grid-cols-maxFrMax py-4 px-6 border border-light-strokeLanding dark:border-dark-strokeLanding rounded-2xl">
					<SvgIcon
						Icon={theme === "light" ? LogoLightNavIcon : DarkWhiteLogoIcon}
						width={92}
						height={32}
					/>

					<div className="grid grid-flow-col gap-6 auto-cols-max place-items-center">
						<P size="p3" type="semibold" onClick={() => goOrScroll()}>
							{t("landing.nav.home")}
						</P>

						<P
							size="p3"
							type="semibold"
							onClick={() => goOrScroll("achievements")}
						>
							{t("landing.nav.about")}
						</P>

						<P size="p3" type="semibold" onClick={() => goOrScroll("news")}>
							{t("landing.nav.news")}
						</P>

						<P size="p3" type="semibold" onClick={() => goOrScroll("partners")}>
							{t("landing.nav.partners")}
						</P>

						<P size="p3" type="semibold" onClick={() => goOrScroll("faq")}>
							{t("landing.nav.faq")}
						</P>

						<P
							size="p3"
							type="semibold"
							onClick={() => navigate(ROUTES.HOW_TO_USE)}
						>
							{t("landing.nav.howToUse")}
						</P>
					</div>

					<LanguageSwitcher variant="landing" mode="full" />
				</div>
			</nav>
		</header>
	);
};

export default React.memo(Navbar);
